import { useState, useEffect } from 'react'

/**
 * Network Information API types
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
interface NetworkInformation extends EventTarget {
  /** Effective connection type: 'slow-2g', '2g', '3g', or '4g' */
  readonly effectiveType?: 'slow-2g' | '2g' | '3g' | '4g'
  /** Whether the user has requested reduced data usage */
  readonly saveData?: boolean
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformation
}

export interface SlowConnectionInfo {
  /** Whether the device has a slow connection (2g or slow-2g) */
  isSlowConnection: boolean
  /** Whether the user has Data Saver enabled */
  saveData: boolean
  /** The effective connection type if available */
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g'
  /** Whether the user prefers reduced motion (can indicate low power mode on iOS) */
  prefersReducedMotion: boolean
}

/**
 * Helper to get reduced motion preference
 */
function getPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

/**
 * Helper to get initial connection info
 */
function getInitialConnectionInfo(): SlowConnectionInfo {
  // SSR-safe initial state
  if (typeof navigator === 'undefined') {
    return {
      isSlowConnection: false,
      saveData: false,
      effectiveType: undefined,
      prefersReducedMotion: false,
    }
  }

  const connection = (navigator as NavigatorWithConnection).connection
  const prefersReducedMotion = getPrefersReducedMotion()

  if (connection) {
    return {
      isSlowConnection: ['slow-2g', '2g'].includes(connection.effectiveType ?? ''),
      saveData: connection.saveData === true,
      effectiveType: connection.effectiveType,
      prefersReducedMotion,
    }
  }

  return {
    isSlowConnection: false,
    saveData: false,
    effectiveType: undefined,
    prefersReducedMotion,
  }
}

/**
 * Hook to detect slow network connections, Data Saver mode, and reduced motion preference.
 * Uses the Network Information API where available.
 *
 * This is useful for:
 * - Skipping video loading on slow connections
 * - Reducing animation complexity
 * - Respecting user's data-saving preferences
 * - Detecting iOS Low Power Mode (via prefersReducedMotion as a proxy)
 *
 * @returns {SlowConnectionInfo} Connection information
 */
function useSlowConnectionDetection(): SlowConnectionInfo {
  const [info, setInfo] = useState<SlowConnectionInfo>(getInitialConnectionInfo)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const connection = (navigator as NavigatorWithConnection).connection
    const motionMediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')

    const updateInfo = () => {
      setInfo({
        isSlowConnection: connection ? ['slow-2g', '2g'].includes(connection.effectiveType ?? '') : false,
        saveData: connection?.saveData === true,
        effectiveType: connection?.effectiveType,
        prefersReducedMotion: getPrefersReducedMotion(),
      })
    }

    // Listen for connection changes
    connection?.addEventListener('change', updateInfo)

    // Listen for reduced motion preference changes
    motionMediaQuery?.addEventListener('change', updateInfo)

    return () => {
      connection?.removeEventListener('change', updateInfo)
      motionMediaQuery?.removeEventListener('change', updateInfo)
    }
  }, [])

  return info
}

export default useSlowConnectionDetection
